import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';

import { postActivitiesMutation } from './api';
import {
  endAndFlushActivities,
  flushActivities,
  registerActivityPoster
} from './buffer';
import { ACTIVITY_FLUSH_INTERVAL_MS } from './constants';

/**
 * Drains the activity buffer for the whole session. Mount once, in the
 * authenticated layout.
 *
 * Three drains, covering the ways a view can end:
 *  - a timer, so a long session reports as it goes;
 *  - `pagehide`, which fires on refresh, tab close and navigation — the open
 *    view is closed there, so a refresh does not lose it;
 *  - `visibilitychange` to hidden, which is the only signal some mobile
 *    browsers give before killing a backgrounded tab. It only flushes what is
 *    already buffered; the open view keeps running, so switching tabs does not
 *    split one view into two rows.
 *
 * Unmount (logout) drains as well.
 */
export const useActivityReporter = () => {
  const { mutateAsync: postActivities } = useMutation(postActivitiesMutation());

  useEffect(() => {
    registerActivityPoster(postActivities);
    const interval = setInterval(
      () => flushActivities(),
      ACTIVITY_FLUSH_INTERVAL_MS
    );

    const handlePageHide = () => endAndFlushActivities({ unload: true });
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden')
        flushActivities({ unload: true });
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Drain before unregistering, so the last batch still goes through the
      // mutation rather than the unload fallback.
      endAndFlushActivities();
      registerActivityPoster(null);
    };
  }, [postActivities]);
};
